import "./ContactUsPage.scss";
import {environment} from "../../environment";
import {GoogleMap, Marker, useJsApiLoader} from "@react-google-maps/api";
import {useCallback, useEffect, useRef, useState} from "react";
import useAppSelector from "../../hooks/useAppSelector";
import markSvg from "../../assets/images/icons/mark.svg";
import phoneSvg from "../../assets/images/icons/phone.svg";
import facebookSvg from "../../assets/images/icons/facebook.svg";
import instagramSvg from "../../assets/images/icons/instagram.svg";
import mailSvg from "../../assets/images/icons/mail.svg";
import {openMessage} from "../../store/notification/notificationSlice";
import {$clientApi} from "../../http";
import {useTranslation} from "react-i18next";
import {Form, FormInstance, Input} from "antd";
import useAppDispatch from "../../hooks/useAppDispatch";
import CustomButton from "../../components/UI/CustomButton/CustomButton";
import ConfirmSendMessagePopup from "./ConfirmSendMessagePopup/ConfirmSendMessagePopup";
import {GymSettingsService} from "../../services/gymSettings.service";
import {IGymDetails} from "../../models/GymSettings";

const defaultOptions = {
    panControl: true,
    zoomControl: true,
    mapTypeControl: false,
    scaleControl: false,
    streetViewControl: false,
    rotateControl: false,
    clickableIcons: false,
    keyboardShortcuts: false,
    // scrollwheel: true,
    disableDoubleClickZoom: false,
    fullscreenControl: false,
    // gestureHandling: 'greedy',
    // styles: defaultMapTheme
}

const containerStyle = {
    width: '100%',
    minHeight: '40vh'
};

const ContactUsPage = () => {

    const dispatch = useAppDispatch();

    const {activeGym} = useAppSelector(state => state.multiGymsSlice);

    const [confirmPopupVisible, setConfirmPopupVisible] = useState<boolean>(false);
    const [gymDetails, setGymDetails] = useState<IGymDetails | null>(null);

    useEffect(() => {
        getGymDetails()
    }, [activeGym]);

    const getGymDetails = async () => {
        try {
            const response = await GymSettingsService.getGymLocation();
            setGymDetails(response.data.data)
        } catch (e) {}
    }

    const {isLoaded} = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: environment.googleMapsApiKey,
        libraries: ["maps"]
    });
    const {content} = useAppSelector(state => state.siteContentSlice)


    const formRef = useRef<FormInstance | null>(null);


    const {t} = useTranslation();
    const mapRef = useRef<any>(null);

    const onLoad = useCallback((map: any) => {
        mapRef.current = map;
    }, [])

    const onUnmount = useCallback(() => {
        mapRef.current = null;
    }, [])

    const onFinish = async (values: {name: string, email: string, message: string}) => {
        const response = await $clientApi.post<boolean>("api/website-integration/contact-us-message", values);
        if (response.data) {
            setConfirmPopupVisible(true);
        } else {
            dispatch(openMessage({type: "error", title: t("MessageCouldNotBeCreated")}));
        }
        formRef.current?.resetFields();
    }

    const closeConfirmMessagePopup = () => {
        setConfirmPopupVisible(false)
    }

    return (
        <>
            <div className={"contact-us-page"}>
            <section className={"map-wrapper"}>
                {
                    isLoaded && (
                        <>
                            <GoogleMap
                                ref={mapRef}
                                center={{lat: gymDetails?.lat || 39.07, lng: gymDetails?.long || 21.81}}
                                zoom={14}
                                onLoad={onLoad}
                                onUnmount={onUnmount}
                                options={defaultOptions}
                                mapContainerStyle={containerStyle}
                            >
                                {gymDetails && (
                                    <Marker position={{lat: gymDetails.lat, lng: gymDetails.long}}/>
                                )}
                            </GoogleMap>
                            {
                                gymDetails && (
                                    <div className="location">
                                        <img src={markSvg} alt={'mark'}/>
                                        <a>{gymDetails.streetNumber} {gymDetails.street} {gymDetails.city}, {gymDetails.country} {gymDetails.postcode}</a>
                                    </div>
                                )
                            }
                            <div className="number">
                                <img src={phoneSvg} alt={'phone number'}/>
                                <a target={"_blank"} href={`tel:${content.contactUsPhoneNumber}`}>{content.contactUsPhoneNumber}</a>
                            </div>
                            <div className="socials">
                                <a target={"_blank"} href={content.contactUsFacebookLink}>
                                    <img src={facebookSvg} alt={'facebook'}/>
                                </a>
                                <a target={"_blank"} href={content.contactUsInstagramLink}>
                                    <img src={instagramSvg} alt={'instagram'}/>
                                </a>
                                <a target={"_blank"} href={`mailto:${content.contactUsGmailLink}`}>
                                    <img src={mailSvg} alt={'gmail'}/>
                                </a>
                            </div>
                        </>
                    )
                }
            </section>
            <section className={"form-wrapper"}>

                <h3 className={"title"}>{t("ContactUs")}</h3>
                <Form
                    ref={formRef}
                    className="form"
                    name="contact-us"
                    initialValues={{remember: true}}
                    onFinish={onFinish}
                    autoComplete="off"
                >

                    <div className={"row"}>
                        <Form.Item
                            className="form-item"
                            name="name"
                            rules={[{required: true, message: t("PleaseInputName")}]}
                        >
                            <Input placeholder={t("Name")} className="form-item__input"/>
                        </Form.Item>
                        <Form.Item
                            className="form-item"
                            name="email"
                            rules={[{required: true, type: "email", message: t("PleaseInputYourEmailAddress")}]}
                        >
                            <Input type={"email"} placeholder={t("Email")} className="form-item__input"/>
                        </Form.Item>
                    </div>

                    <Form.Item
                        className="form-item"
                        name="phoneNumber"
                        rules={[{required: true, message: t("PleaseInputYourPhone")}]}
                    >
                        <Input placeholder={t("PhoneNumber")} className="form-item__input"/>
                    </Form.Item>

                    <Form.Item
                        className="form-item textarea"
                        name="message"
                        rules={[{required: true, message: t("PleaseInputMessage")}]}>

                        <Input.TextArea autoSize={true} placeholder={t("Message")} className="form-item__textarea"/>
                    </Form.Item>

                    <div className={"btn-wrapper"}>
                        <CustomButton
                            type="submit"
                            typeStyle={"fill"}
                            text={t("Send")}
                            borderRadius={false}
                        />
                    </div>

                </Form>

            </section>
        </div>
            {
                confirmPopupVisible && (
                    <ConfirmSendMessagePopup closePopup={closeConfirmMessagePopup}/>
                )
            }
        </>
    )

}

export default ContactUsPage;